import React, { useState } from 'react';
import {
  View, Text, StyleSheet, TouchableOpacity, Platform, ScrollView, SafeAreaView, StatusBar,
  KeyboardAvoidingView
} from 'react-native';
import { 
  Button, 
  Input, 
  PasswordInput, 
  PhoneInput, 
  Logo, 
  SocialLogin, 
  DualPaneLayout,
  colors, 
  spacing, 
  typography 
} from '../components';
import { useAppContext } from '../contexts/AppContext';
import { useAppStore } from '../store/appStore';
import { apiService } from '../services/api';
import { RegisterRequest } from '../services/api';
import { handleApiError, logError } from '../utils/errorHandler';
import { useLocale } from '../contexts/LocaleContext';
import { useTypography } from '../hooks/useTypography';
import { useThemedStyles } from '../hooks/useThemedStyles';

export const RegisterScreen: React.FC = () => {
  const styles = useThemedStyles(getStyles);
  const { 
    handleBackToLogin: onBack, 
    handleBackToLogin: onLogin, 
    handleSocialLogin,
    navigateTo,
    showNotification
  } = useAppContext();
  
  const { setPendingEmail, setIsNewUser } = useAppStore();
  const { t, isRTL } = useLocale();
  const typo = useTypography();

  const [isLoading, setIsLoading] = useState(false);

  // Social login handlers
  const onGoogleLogin = () => handleSocialLogin('google');
  const onFacebookLogin = () => handleSocialLogin('facebook');
  const onAppleLogin = () => handleSocialLogin('apple');
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    password: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = t('validation.required');
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = t('validation.required');
    }

    if (!formData.email.trim()) {
      newErrors.email = t('validation.required');
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t('validation.emailInvalid');
    }

    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = t('validation.required');
    } else if (formData.phoneNumber.length < 5) {
      newErrors.phoneNumber = t('validation.phoneInvalid');
    }

    if (!formData.password.trim()) {
      newErrors.password = t('validation.required');
    } else if (formData.password.length < 6) {
      newErrors.password = t('validation.passwordMin');
    }

    if (!formData.confirmPassword.trim()) {
      newErrors.confirmPassword = t('validation.required');
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = t('validation.passwordMatch');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleRegister = async (userData: RegisterRequest) => {
    try {
      logError('Starting registration with data:', 'RegisterScreen');
      
      const response = await apiService.register(userData);
      logError('Registration response:', 'RegisterScreen');
      
      if (response.statusCode === 1000) {
        // Set shared state in centralized store
        setPendingEmail(userData.email);
        setIsNewUser(true);
        
        // Navigate to OTP verification
        navigateTo('otp-verification');
        
        // Show success notification
        showNotification('success', 'Registration Successful', 'Please check your email for verification code.');
      } else {
        // Handle API error with unified error handling
        const { title, message, type } = handleApiError(response);
        showNotification(type, title, message);
        throw new Error(message);
      }
    } catch (error: any) {
      logError(error, 'RegisterScreen');
      
      // Handle error with unified error handling
      const { title, message, type } = handleApiError(error);
      showNotification(type, title, message);
      throw error;
    }
  };

  const handleBackToLogin = () => {
    if (onLogin) {
      onLogin(); // Use context method
    }
  };

  // Note: iOS restriction removed per App Store guidelines - registration is now fully functional on iOS

  return (
    <DualPaneLayout
      title="DISLIX"
      subtitle={t('auth.joinSubtitle')}
    >
      <SafeAreaView style={styles.container}>
        <StatusBar barStyle="dark-content" backgroundColor={colors.white} />
        
        <KeyboardAvoidingView 
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
          style={styles.keyboardAvoidingView}
          keyboardVerticalOffset={Platform.OS === 'ios' ? 0 : 20}
        >
          {/* Header */}
          <View style={styles.header}>
            <Logo size="medium" variant="white" />
          </View>

          {/* Main Content */}
          <ScrollView contentContainerStyle={styles.content}>
            <Text style={[styles.title, { fontFamily: typo.fontFamily.bold }]}>{t('auth.createAccount')}</Text>
            <Text style={[styles.subtitle, { fontFamily: typo.fontFamily.primary }]}>{t('auth.joinSubtitle')}</Text>

            {/* Registration Form */}
            <View style={styles.form}>
              <Input
                label={t('auth.firstName')}
                value={formData.firstName}
                onChangeText={(text: string) => setFormData(prev => ({ ...prev, firstName: text }))}
                placeholder={t('auth.firstNamePlaceholder')}
                autoCapitalize="words"
                autoCorrect={false}
                error={errors.firstName}
                editable={!isLoading}
              />

              <Input
                label={t('auth.lastName')}
                value={formData.lastName}
                onChangeText={(text: string) => setFormData(prev => ({ ...prev, lastName: text }))}
                placeholder={t('auth.lastNamePlaceholder')}
                autoCapitalize="words"
                autoCorrect={false}
                error={errors.lastName}
                editable={!isLoading}
              />

              <Input
                label={t('auth.email')}
                value={formData.email}
                onChangeText={(text: string) => setFormData(prev => ({ ...prev, email: text }))}
                placeholder={t('auth.emailPlaceholder')}
                keyboardType="email-address"
                autoCapitalize="none"
                autoCorrect={false}
                error={errors.email}
                editable={!isLoading}
              />

              <PhoneInput
                label={t('auth.phoneNumber')}
                value={formData.phoneNumber}
                onChangeText={(text: string) => setFormData(prev => ({ ...prev, phoneNumber: text }))}
                placeholder={t('auth.phoneNumberPlaceholder')}
                error={errors.phoneNumber}
                disabled={isLoading}
              />

              <PasswordInput
                label={t('auth.password')}
                value={formData.password}
                onChangeText={(text: string) => setFormData(prev => ({ ...prev, password: text }))}
                placeholder={t('auth.createPasswordPlaceholder')}
                error={errors.password}
                editable={!isLoading}
              />

              <PasswordInput
                label={t('auth.confirmPassword')}
                value={formData.confirmPassword}
                onChangeText={(text: string) => setFormData(prev => ({ ...prev, confirmPassword: text }))}
                placeholder={t('auth.confirmPasswordPlaceholder')}
                error={errors.confirmPassword}
                editable={!isLoading}
              />

              <Button
                title={t('auth.createAccount')}
                onPress={() => {
                  if (validateForm()) {
                    setIsLoading(true);
                    
                    const userData = {
                      name: `${formData.firstName} ${formData.lastName}`,
                      email: formData.email,
                      phoneNumber: formData.phoneNumber,
                      password: formData.password,
                    };
                    
                    handleRegister(userData).catch((error) => {
                      // Error is handled by notification system
                    }).finally(() => {
                      setIsLoading(false);
                    });
                  }
                }}
                variant="primary"
                size="lg"
                fullWidth
                loading={isLoading}
              />
            </View>

            {/* Social Login */}
            <SocialLogin
              onGoogleLogin={onGoogleLogin}
              onFacebookLogin={onFacebookLogin}
              onAppleLogin={onAppleLogin}
              isLoading={isLoading}
            />

            {/* Back to Login Link */}
            <View style={[styles.backToLoginContainer, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
              <Text style={[styles.backToLoginText, { fontFamily: typo.fontFamily.primary }]}>{t('auth.hasAccount')}</Text>
              <TouchableOpacity onPress={handleBackToLogin} disabled={isLoading}>
                <Text style={[styles.backToLoginLink, { fontFamily: typo.fontFamily.medium }, isLoading && styles.disabledText]}>{t('auth.signIn')}</Text>
              </TouchableOpacity>
            </View>
          </ScrollView>
        </KeyboardAvoidingView>
      </SafeAreaView>
    </DualPaneLayout>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background.surface,
  },
  keyboardAvoidingView: {
    flex: 1,
  },
  header: {
    alignItems: 'center',
    paddingHorizontal: spacing.screenPadding,
    paddingTop: spacing.sm,
    paddingBottom: spacing.md,
  },
  logoContainer: {
    alignItems: 'center',
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: spacing.screenPadding,
    paddingTop: spacing.lg,
    paddingBottom: spacing.xl,
  },
  content: {
    flexGrow: 1,
    paddingHorizontal: spacing.screenPadding,
    paddingTop: spacing.xl,
    paddingBottom: spacing.xl,
  },
  title: {
    fontSize: typography.fontSize['2xl'],
    fontWeight: '700', // bold
    color: colors.textDark,
    textAlign: 'center',
    marginBottom: spacing.lg,
  },
  subtitle: {
    fontSize: typography.fontSize.base,
    fontWeight: '400', // normal
    color: colors.textLight,
    textAlign: 'center',
    marginBottom: spacing.xl,
  },
  form: {
    marginBottom: spacing.xl,
  },
  registerButton: {
    width: '100%',
  },

  loginContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  loginText: {
    fontSize: typography.fontSize.base,
    color: colors.textLight,
    fontFamily: typography.fontFamily.primary,
  },
  loginLink: {
    fontSize: typography.fontSize.base,
    color: colors.primary,
    fontWeight: '500',
    fontFamily: typography.fontFamily.medium,
  },
  backToLoginContainer: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  backToLoginText: {
    fontSize: typography.fontSize.base,
    color: colors.textLight,
  },
  backToLoginLink: {
    fontSize: typography.fontSize.base,
    color: colors.primary,
    fontWeight: '500',
  },
  disabledText: {
    opacity: 0.7,
  },
  label: {
    fontSize: typography.fontSize.sm,
    fontWeight: '500', // medium
    color: colors.textDark,
    marginBottom: spacing.xs,
    fontFamily: typography.fontFamily.medium,
  },
  input: {
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: spacing.borderRadius.md,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.md,
    fontSize: typography.fontSize.base,
    color: colors.textDark,
    backgroundColor: colors.background.surface,
    fontFamily: typography.fontFamily.primary,
  },
});
